/**
 * Where the back link in a builder chat should go.
 *
 * Derived from WHAT is being built, not from where the user happened to
 * navigate from. Two reasons that is the better signal:
 *
 * 1. It is trustworthy. A remembered origin has to survive the URL — route
 *    state is wiped after mount and dropped again on the first send — which
 *    makes it attacker-editable and an open-redirect surface needing
 *    validation. The entity type is already carried by the conversation.
 * 2. It is right more often. Someone who opened the builder from a record, a
 *    shared link or the Builds tab still wants the list of the thing they were
 *    building, not the arbitrary page they came from.
 *
 * Each buildable kind gets an explicit entry. Deliberately NOT derived from
 * `entityTypeRegistry`'s `routes.list`: automations are reachable at
 * `/:orgId/crm/ahoy_event_automation` per the registry, but their real home is
 * now the top-level `/:orgId/automations`, and validation rules live in
 * settings rather than in a CRM list at all. An explicit map is honest about
 * that; a derived one would quietly send people to the wrong place.
 */

import {
  AUTOMATION_ENTITY_TYPE,
  SCHEDULED_AUTOMATION_ENTITY_TYPE,
  VALIDATION_RULE_ENTITY_TYPE,
} from '@/lib/reasoning/entityTypes';

export interface BuilderBackDestination {
  /** In-app path to navigate to. */
  path: string;
  /**
   * Section name for the breadcrumb, and the basis of the button's accessible
   * name — with no visible label, that is all a screen reader announces.
   */
  label: string;
}

/**
 * Fallback for a builder whose subject is not yet known, or a kind with no
 * list of its own. Conversations is where the chat's own back button has
 * always gone, so this is today's behaviour rather than a new guess.
 */
function conversationsFallback(orgId: string | number): BuilderBackDestination {
  return { path: `/${orgId}/conversations`, label: 'Chat' };
}

export function getBuilderBackDestination(
  orgId: string | number | undefined,
  entityTypeName: string | null | undefined
): BuilderBackDestination | null {
  // Without an org there is no in-app path to build, so offer nothing rather
  // than navigate somewhere wrong.
  if (orgId === undefined || orgId === null || orgId === '') {
    return null;
  }

  switch (entityTypeName) {
    case AUTOMATION_ENTITY_TYPE:
    case SCHEDULED_AUTOMATION_ENTITY_TYPE:
      // Both automation kinds share the /automations home.
      return { path: `/${orgId}/automations`, label: 'Automations' };
    case VALIDATION_RULE_ENTITY_TYPE:
      // Validation rules are configuration, so they stayed in settings when
      // automations moved out to their own nav entry.
      return {
        path: `/${orgId}/settings/validation-rules`,
        label: 'Validation rules',
      };
    default:
      return conversationsFallback(orgId);
  }
}
